import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { Club, ClubAcceptableUsers } from 'Src/models/club';
import { TwoPanelEditor } from 'Src/components/TwoPanelEditor/TwoPanelEditor';
import { Loader } from 'Src/components/Loader/Loader';
import { SendRequestPageShell } from 'Src/components/SendRequestPageShell/SendRequestPageShell';
import { ColumnDefinition } from 'Src/helpers/table/table';
import { FILTER_TYPE } from 'Src/consts/table';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { getClubStudentsCountPost, getClubStudentsPost } from 'Src/helpers/service/admin/clubStudents';
import {
  getSelectOptionForAge,
  getSelectOptionForGender,
  getSelectOptionsForClubPriority,
  getSelectOptionsForClubMessagesStatus
} from 'Src/helpers/table/select';
import { getUserGenderArrayConvertedFromEventGender } from 'Src/helpers/tournament/tournament';
import { CLUB_MESSAGE_INVITE_STATUS_ALL, CLUB_MESSAGE_INVITE_STATUS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/club';
import { formatAgesCompactForSchool } from 'Src/helpers/ageGroups';
import './ClubParticipantsEditor.css';
import '../../TeamsAndEvents/Events/SchoolEventManager.scss';

interface Props {
  user: AppUser;
  club: Club;
  invitationSummary: {
    eligible: number;
    sent: number;
    accepted: number;
    rejected: number;
    pending: number;
    notSent: number;
  };
  isInvitationSummaryLoading: boolean;
  onBack: (hasUnsavedChanges?: boolean) => void;
  onNext: (students: any[], useAllEligible: boolean, recipientCount: number) => void;
}

const PAGE_SIZE = 20;
const keyOf = (student: any) => `${student.userId || student.id}:${student.permissionId}`;
const statusOf = (student: any) => student.clubMessageStatus || student.messageStatus || '';
const statusLabelOf = (student: any) => {
  const status = statusOf(student);
  return CLUB_MESSAGE_INVITE_STATUS_SERVER_TO_CLIENT_MAPPING[status] || status;
};
const updatedOf = (student: any) => student.messageUpdatedAt || student.invitationStatusSetAt || student.updatedAt;
const priorityOf = (student: any) => student.messagePriority || student.priority;
const PANEL_COLUMNS: ColumnDefinition[] = [
  { text: 'Name', field: 'firstName', accessor: ['firstName'], type: FILTER_TYPE.TEXT, isSort: false },
  { text: 'Surname', field: 'lastName', accessor: ['lastName'], type: FILTER_TYPE.TEXT, isSort: false },
  { text: 'Parents', field: 'parents', accessor: ['parents'], type: FILTER_TYPE.NONE, isSort: false },
  { text: 'Invitation status', field: 'status', accessor: statusLabelOf, type: FILTER_TYPE.NONE, isSort: false },
  { text: 'Status updated', field: 'updated', accessor: updatedOf, type: FILTER_TYPE.NONE, isSort: false },
  { text: 'Priority', field: 'priority', accessor: priorityOf, type: FILTER_TYPE.NONE, isSort: false }
];

export const ClubInvitationRecipientsStep: React.FC<Props> = ({
  user,
  club,
  invitationSummary,
  isInvitationSummaryLoading,
  onBack,
  onNext
}) => {
  const userId = user.userId || 'anonymous';
  const storageKey = `columnVisibility__${userId}__clubInvitationRecipients`;
  const [forms, setForms] = React.useState<any[]>([]);
  const [students, setStudents] = React.useState<ClubAcceptableUsers[]>([]);
  const studentCache = React.useRef<Map<string, ClubAcceptableUsers>>(new Map());
  const [recipients, setRecipients] = React.useState<Set<string>>(new Set());
  const [allEligibleSelected, setAllEligibleSelected] = React.useState(false);
  const [eligibleSelected, setEligibleSelected] = React.useState<Set<string>>(new Set());
  const [recipientSelected, setRecipientSelected] = React.useState<Set<string>>(new Set());
  const [eligiblePage, setEligiblePage] = React.useState(1);
  const [eligibleTotal, setEligibleTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [pageLoading, setPageLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [priority, setPriority] = React.useState('');
  const presetAges = Array.isArray(club.ages) ? club.ages.map(String) : [];
  const presetGenders = getUserGenderArrayConvertedFromEventGender(club.gender).map(String);
  const [selectedAges, setSelectedAges] = React.useState<string[]>(presetAges);
  const [selectedGenders, setSelectedGenders] = React.useState<string[]>(presetGenders);
  const [hidden, setHidden] = React.useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(storageKey) || '[]'));
    } catch (_) {
      return new Set();
    }
  });

  const buildWhere = React.useCallback(() => {
    const where: any = { isParticipant: false, includeInvitedExceptions: true };
    const needle = search.trim();
    if (needle) {
      where.$or = [{ firstName: { like: needle, options: 'i' } }, { lastName: { like: needle, options: 'i' } }];
    }
    if (status) where.messageStatus = status;
    if (priority) where.messagePriority = Number(priority);
    if (selectedAges.length && forms.length) {
      const formIds = forms.filter(form => selectedAges.includes(String(form.age))).map(form => form.id);
      if (formIds.length) where.formId = { $in: formIds };
    }
    if (selectedGenders.length) where.gender = { $in: selectedGenders };
    return where;
  }, [search, status, priority, selectedAges.join(','), selectedGenders.join(','), forms]);

  const loadPage = React.useCallback(
    (page: number, recipientKeys: Set<string> = recipients) => {
      setPageLoading(true);
      setLoadError('');
      const where = buildWhere();
      Promise.all([
        getClubStudentsPost(user, club.id, { where, limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE } as any),
        getClubStudentsCountPost(user, club.id, where)
      ])
        .then(([rows, count]) => {
          const pageRows: ClubAcceptableUsers[] = Array.isArray(rows) ? rows : [];
          pageRows.forEach(row => studentCache.current.set(keyOf(row), row));
          const selectedRows = Array.from(studentCache.current.values()).filter(row => recipientKeys.has(keyOf(row)));
          const byKey = new Map<string, ClubAcceptableUsers>();
          selectedRows.concat(pageRows).forEach(row => byKey.set(keyOf(row), row));
          setStudents(Array.from(byKey.values()));
          setEligibleTotal(count.count || 0);
          setEligiblePage(page);
          setEligibleSelected(new Set());
        })
        .catch(() => setLoadError('Eligible students could not be loaded. Please try again.'))
        .then(() => {
          setPageLoading(false);
          setLoading(false);
        });
    },
    [user, club.id, buildWhere, recipients]
  );

  React.useEffect(() => {
    setLoading(true);
    getAllForms(user)
      .then(schoolForms => setForms(schoolForms || []))
      .catch(() => setForms([]));
  }, [user]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => loadPage(1), 350);
    return () => window.clearTimeout(timer);
  }, [search, status, priority, selectedAges.join(','), selectedGenders.join(','), forms.length]);

  const toggleColumn = (field: string) => {
    if (field === 'firstName' || field === 'lastName') return;
    const next = new Set(hidden);
    next.has(field) ? next.delete(field) : next.add(field);
    setHidden(next);
    localStorage.setItem(storageKey, JSON.stringify(Array.from(next)));
  };
  const resetColumns = () => {
    setHidden(new Set());
    localStorage.setItem(storageKey, '[]');
  };
  const ageGroupsNaming = user.activeSchool && user.activeSchool.ageGroupsNaming;
  const panelFilterOptions: any = {
    ages: getSelectOptionForAge(forms, ageGroupsNaming),
    gender: getSelectOptionForGender(),
    status: getSelectOptionsForClubMessagesStatus(user).filter(option => option.text && option.value),
    priority: getSelectOptionsForClubPriority().filter(option => option.text && option.value !== '')
  };
  const decorate = (student: any) => {
    const parents = (student.parents || []).map(parent => `${parent.firstName} ${parent.lastName}`).join(', ');
    const studentClass = propz.get(student, ['form', 'name'], '') || propz.get(student, ['form', 'ageGroup'], '');
    const studentHouse = propz.get(student, ['house', 'name'], '');
    const details = [
      studentClass ? `Class: ${studentClass}` : 'Class: Not set',
      studentHouse ? `House: ${studentHouse}` : 'House: Not set',
      !hidden.has('parents') && parents ? `Parents: ${parents}` : '',
      !hidden.has('status') && statusOf(student) ? `Status: ${statusLabelOf(student)}` : '',
      !hidden.has('updated') && updatedOf(student)
        ? `Updated: ${moment(updatedOf(student)).format('DD/MM/YYYY HH:mm')}`
        : '',
      !hidden.has('priority') && priorityOf(student) ? `Priority: ${priorityOf(student)}` : ''
    ].filter(Boolean);
    const eligibilityWarnings: string[] = [];
    const studentAge = Number(student.form && student.form.age);
    if (Number.isFinite(studentAge) && !presetAges.includes(String(studentAge))) {
      eligibilityWarnings.push(
        `Age: club is ${formatAgesCompactForSchool(
          club.ages,
          user.activeSchool
        )}, student is ${formatAgesCompactForSchool([studentAge], user.activeSchool)}`
      );
    }
    const studentGender = String(student.gender || '');
    if (club.gender === 'MALE_ONLY' && studentGender === 'FEMALE') {
      eligibilityWarnings.push('Gender: club is for boys, student is female');
    }
    if (club.gender === 'FEMALE_ONLY' && studentGender === 'MALE') {
      eligibilityWarnings.push('Gender: club is for girls, student is male');
    }
    return { ...student, transferMeta: details.join(' · '), eligibilityWarning: eligibilityWarnings.join(' · ') };
  };
  const eligibleRows =
    allEligibleSelected && recipients.size > 0
      ? []
      : students.filter(student => !recipients.has(keyOf(student))).map(decorate);
  const recipientRows = Array.from(studentCache.current.values())
    .filter(student => recipients.has(keyOf(student)))
    .map(decorate);
  const eligibleSelectedRows = eligibleRows.filter(student => eligibleSelected.has(keyOf(student)));
  const recipientSelectedRows = recipientRows.filter(student => recipientSelected.has(keyOf(student)));
  const explicitPendingCount = recipientRows.filter(
    student => statusOf(student) === CLUB_MESSAGE_INVITE_STATUS_ALL.PENDING
  ).length;
  const explicitNotSentCount = recipientRows.filter(
    student => statusOf(student) === CLUB_MESSAGE_INVITE_STATUS_ALL.NOT_SENT
  ).length;
  const willUpdateCount = allEligibleSelected ? invitationSummary.pending : explicitPendingCount;
  const willSendCount = allEligibleSelected
    ? invitationSummary.pending + invitationSummary.notSent
    : explicitPendingCount + explicitNotSentCount;
  const answeredCount = invitationSummary.accepted + invitationSummary.rejected;
  const isDefaultEligibleFilter =
    !search.trim() &&
    !status &&
    !priority &&
    selectedAges
      .map(String)
      .sort()
      .join(',') ===
      presetAges
        .map(String)
        .sort()
        .join(',') &&
    selectedGenders
      .map(String)
      .sort()
      .join(',') ===
      presetGenders
        .map(String)
        .sort()
        .join(',');

  const toggleSelection = (key: string, side: 'eligible' | 'recipient') => {
    const source = side === 'eligible' ? eligibleSelected : recipientSelected;
    const setter = side === 'eligible' ? setEligibleSelected : setRecipientSelected;
    const next = new Set(source);
    next.has(key) ? next.delete(key) : next.add(key);
    setter(next);
  };
  const move = (direction: 'add' | 'remove') => {
    if (direction === 'add' && allEligibleSelected) {
      setRecipients(new Set(eligibleRows.map(keyOf)));
      setEligibleSelected(new Set());
      setRecipientSelected(new Set());
      return;
    }
    if (direction === 'remove' && allEligibleSelected) setAllEligibleSelected(false);
    const next = new Set(recipients);
    const selected = direction === 'add' ? eligibleSelected : recipientSelected;
    const shouldLoadNextEligiblePage =
      direction === 'add' &&
      eligibleRows.length > 0 &&
      eligibleRows.every(student => selected.has(keyOf(student))) &&
      eligiblePage * PAGE_SIZE < eligibleTotal;
    selected.forEach(key => (direction === 'add' ? next.add(key) : next.delete(key)));
    setRecipients(next);
    setEligibleSelected(new Set());
    setRecipientSelected(new Set());
    if (shouldLoadNextEligiblePage) loadPage(eligiblePage + 1, next);
  };
  const moveSingle = (student: any, direction: 'add' | 'remove') => {
    if (direction === 'remove' && allEligibleSelected) setAllEligibleSelected(false);
    const next = new Set(recipients);
    const shouldLoadNextEligiblePage =
      direction === 'add' && eligibleRows.length === 1 && eligiblePage * PAGE_SIZE < eligibleTotal;
    direction === 'add' ? next.add(keyOf(student)) : next.delete(keyOf(student));
    setRecipients(next);
    setEligibleSelected(new Set());
    setRecipientSelected(new Set());
    if (shouldLoadNextEligiblePage) loadPage(eligiblePage + 1, next);
  };

  if (loading) return <Loader />;
  if (loadError && !students.length) {
    return (
      <SendRequestPageShell
        title="Send invitations"
        subtitle={club.name}
        onBack={() => onBack(false)}
        backLabel="Back to clubs"
        className="SendRequestPageShell--clubTransfer"
      >
        <div className="clubParticipantsEditor clubParticipantsEditor--embedded clubParticipantsEditor__loadError">
          <span className="clubParticipantsEditor__errorIcon" aria-hidden="true">
            !
          </span>
          <strong>Unable to load eligible students</strong>
          <span>{loadError}</span>
          <button className="clubParticipantsEditor__save" onClick={() => loadPage(1)}>
            Try again
          </button>
        </div>
      </SendRequestPageShell>
    );
  }

  return (
    <SendRequestPageShell
      title="Send invitations"
      subtitle={club.name}
      onBack={() => onBack(Boolean(recipients.size || allEligibleSelected))}
      backLabel="Back to clubs"
      className="SendRequestPageShell--clubTransfer SendRequestPageShell--clubInviteRecipients"
      headerExtra={
        <div className="clubInviteStats" aria-label="Invitation summary" aria-live="polite">
          <div className="clubInviteStats__item isEligible">
            <span>Eligible</span>
            <strong>{eligibleTotal}</strong>
          </div>
          <div className="clubInviteStats__item isSent">
            <span>Sent</span>
            <strong>{isInvitationSummaryLoading ? '—' : invitationSummary.sent}</strong>
          </div>
          <div className="clubInviteStats__item isAnswered">
            <span>Answered</span>
            <strong>{isInvitationSummaryLoading ? '—' : answeredCount}</strong>
          </div>
          <div className="clubInviteStats__item isWillSend">
            <span>Will send</span>
            <strong>{isInvitationSummaryLoading && allEligibleSelected ? '—' : willSendCount}</strong>
          </div>
          <div className="clubInviteStats__item isUpdate">
            <span>Will update</span>
            <strong>{isInvitationSummaryLoading && allEligibleSelected ? '—' : willUpdateCount}</strong>
          </div>
        </div>
      }
    >
      <div className="clubParticipantsEditor clubParticipantsEditor--embedded clubInviteRecipientsEditor">
        <section className="bSchoolTeamManager__participantsCard clubTransferPage__card">
          <div className="bSchoolTeamManager__editorWrap clubParticipantsEditor__editorWrap">
            <TwoPanelEditor
              isSourceFirst
              items={eligibleRows}
              itemsTitle="Eligible students"
              itemsColumns={PANEL_COLUMNS.filter(column => !hidden.has(column.field))}
              itemsSelected={eligibleSelectedRows}
              onItemClick={index => toggleSelection(keyOf(eligibleRows[index]), 'eligible')}
              itemsFilters={{
                ages: selectedAges,
                gender: selectedGenders,
                status: status ? [status] : [],
                priority: priority ? [priority] : []
              }}
              onItemsFilterChange={() => undefined}
              isShowItemsFilter={filtersOpen}
              isItemsFiltered={!!(status || priority)}
              onItemsFilterClick={event => {
                event.preventDefault();
                setFiltersOpen(value => !value);
              }}
              itemsFiltersOptions={panelFilterOptions}
              itemsPresetFilters={{ ages: presetAges, gender: presetGenders }}
              itemsPresetSourceLabel="Club settings"
              itemsPresetFiltersLocked={false}
              onReplaceItemsFilters={(filters: any) => {
                setSelectedAges(filters.ages || presetAges);
                setSelectedGenders(filters.gender || presetGenders);
                setStatus((filters.status || [])[0] || '');
                setPriority((filters.priority || [])[0] || '');
              }}
              itemsAccumulator={recipientRows}
              itemsAccumulatorTitle="Invitation recipients"
              itemsAccumulatorColumns={PANEL_COLUMNS.filter(column => !hidden.has(column.field))}
              itemsAccumulatorSelected={recipientSelectedRows}
              onItemAccumulatorClick={index => toggleSelection(keyOf(recipientRows[index]), 'recipient')}
              itemsAccumulatorFilters={{}}
              onItemsAccumulatorFilterChange={() => undefined}
              isShowItemsAccumulatorFilter={false}
              isItemsAccumulatorFiltered={false}
              onItemsAccumulatorFilterClick={event => event.preventDefault()}
              itemsAccumulatorFiltersOptions={{}}
              onAddClick={() => move('add')}
              onRemoveClick={() => move('remove')}
              onAddSingleItem={student => moveSingle(student, 'add')}
              onRemoveSingleItem={student => moveSingle(student, 'remove')}
              itemsCount={eligibleTotal}
              itemsAccumulatorCount={allEligibleSelected ? eligibleTotal : recipientRows.length}
              itemCurrentPage={eligiblePage}
              itemAccumulatorCurrentPage={1}
              itemsSetCurrentPageParams={loadPage}
              itemsOnApplyFilterClick={() => loadPage(1)}
              itemsOnClearFilterClick={() => {
                setSelectedAges(presetAges);
                setSelectedGenders(presetGenders);
                setStatus('');
                setPriority('');
              }}
              itemsAccumulatorOnApplyFilterClick={() => undefined}
              itemsAccumulatorOnClearFilterClick={() => undefined}
              onSelectAllItemsClick={() => {
                setAllEligibleSelected(true);
                setEligibleSelected(new Set(eligibleRows.map(keyOf)));
              }}
              onSelectAllItemsOnPageClick={() =>
                setEligibleSelected(current => new Set([...Array.from(current), ...eligibleRows.map(keyOf)]))
              }
              onUnselectAllItemsClick={() => {
                setAllEligibleSelected(false);
                setEligibleSelected(new Set());
              }}
              isSelectAllItemsChecked={
                allEligibleSelected || (eligibleRows.length > 0 && eligibleSelectedRows.length === eligibleRows.length)
              }
              onSelectAllItemsAccumulatorClick={() =>
                setRecipientSelected(current => new Set([...Array.from(current), ...recipientRows.map(keyOf)]))
              }
              onSelectAllItemsAccumulatorOnPageClick={() =>
                setRecipientSelected(current => new Set([...Array.from(current), ...recipientRows.map(keyOf)]))
              }
              onUnselectAllItemsAccumulatorClick={() => setRecipientSelected(new Set())}
              isSelectAllItemsAccumulatorChecked={
                recipientRows.length > 0 && recipientSelectedRows.length === recipientRows.length
              }
              sortItemsDirection=""
              sortItemsColumnsName=""
              onSortItemsClick={() => undefined}
              sortItemsAccumulatorDirection=""
              sortItemsAccumulatorColumnsName=""
              onSortItemsAccumulatorClick={() => undefined}
              itemsAddBuffer={recipientRows}
              itemsRemoveBuffer={[]}
              isItemsLoading={loading || pageLoading}
              onItemsSearchChange={setSearch}
              itemsSearchValue={search}
              itemsModernPagination
              itemsAllowSelectAllFiltered={isDefaultEligibleFilter}
              itemsAllColumns={PANEL_COLUMNS}
              itemsHiddenFields={hidden}
              onToggleItemsColumn={toggleColumn}
              isItemsColumnToggleable={field => field !== 'firstName' && field !== 'lastName'}
              onResetItemsColumns={resetColumns}
              itemsAccumulatorAllColumns={PANEL_COLUMNS}
              itemsAccumulatorHiddenFields={hidden}
              onToggleItemsAccumulatorColumn={toggleColumn}
              isItemsAccumulatorColumnToggleable={field => field !== 'firstName' && field !== 'lastName'}
              onResetItemsAccumulatorColumns={resetColumns}
            />
          </div>
        </section>
        <footer className="clubTransferPage__footer">
          <div>
            <span>
              {allEligibleSelected
                ? `All ${eligibleTotal} eligible students will be invited`
                : recipients.size
                ? `${recipients.size} recipient${recipients.size === 1 ? '' : 's'} selected`
                : 'Select at least one recipient to continue'}
            </span>
          </div>
          <button type="button" onClick={() => onBack(Boolean(recipients.size || allEligibleSelected))}>
            Cancel
          </button>
          <button
            type="button"
            className="clubParticipantsEditor__save"
            disabled={!recipientRows.length && !allEligibleSelected}
            onClick={() =>
              onNext(recipientRows, allEligibleSelected, allEligibleSelected ? eligibleTotal : recipientRows.length)
            }
          >
            Continue to booking form
          </button>
        </footer>
      </div>
    </SendRequestPageShell>
  );
};
